<template>
  <div v-if="isOpen" class="absolute inset-0 z-10 flex flex-col">
    <!-- Backdrop (scoped to chat widget) -->
    <div class="absolute inset-0" @click="$emit('close')" />

    <!-- Dropdown panel -->
    <div
      class="relative z-20 flex flex-col flex-1 min-h-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700"
    >
      <!-- Search input -->
      <div class="p-3 border-b border-gray-200 dark:border-gray-700">
        <input
          ref="searchInputRef"
          v-model="searchQuery"
          type="text"
          placeholder="Search tenants..."
          class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          @keydown.escape="$emit('close')"
        />
      </div>

      <!-- Tenant list -->
      <div class="flex-1 min-h-0 overflow-y-auto">
        <div v-if="filteredTenants.length === 0" class="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
          No tenants found
        </div>
        <button
          v-for="tenant in filteredTenants"
          :key="tenant.id"
          type="button"
          class="w-full px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-start gap-2 border-b border-gray-100 dark:border-gray-700 last:border-b-0"
          @click="handleSelect(tenant)"
        >
          <div class="flex-1 min-w-0">
            <div class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
              {{ tenant.name }}
            </div>
            <div v-if="tenant.tags && tenant.tags.length > 0" class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {{ tenant.tags.map((t) => t.name).join(', ') }}
            </div>
          </div>
          <svg
            v-if="selectedTenantId === tenant.id"
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import type { TenantOption } from '@/components/tenancy-dropdown/type';

const props = defineProps<{
  tenants: TenantOption[];
  selectedTenantId: string | null;
  isOpen: boolean;
}>();

const emit = defineEmits<{
  (e: 'select', tenant: TenantOption): void;
  (e: 'close'): void;
}>();

const searchQuery = ref('');
const searchInputRef = ref<HTMLInputElement | null>(null);

const filteredTenants = computed(() => {
  if (!searchQuery.value.trim()) {
    return props.tenants;
  }

  const query = searchQuery.value.toLowerCase().trim();
  return props.tenants.filter((tenant) => {
    const nameMatch = tenant.name.toLowerCase().includes(query);
    const tagsMatch = tenant.tags?.some((tag) => tag.name.toLowerCase().includes(query));
    return nameMatch || tagsMatch;
  });
});

const handleSelect = (tenant: TenantOption): void => {
  emit('select', tenant);
};

// Auto-focus search input when opened
watch(
  () => props.isOpen,
  (isOpen) => {
    if (isOpen) {
      searchQuery.value = '';
      nextTick(() => {
        searchInputRef.value?.focus();
      });
    }
  },
);
</script>
